import React from 'react';
import { Box, Button, Checkbox, Stack, Text } from '@chakra-ui/react';
import { OktonInput, OktonPanel } from '@okton/platform-ui';
import { ConferenceItem, ConferencePage, listConferences, releaseNoSend } from './aiPlanReconciliationApi';
import { textMoney, textStatuses } from './aiPlanTextApi';
const reasons: Record<string, string> = {
 response_cost_dimension_unknown: 'Campo não reconhecido na validação de custos.',
 response_billing_unsupported: 'Metadados de cobrança não reconhecidos.',
 response_invalid: 'A resposta não passou pela validação.',
 transport_failure: 'Falha de comunicação. Isso, sozinho, não comprova que a geração deixou de ser enviada.',
};
export const AiPlanReconciliationPanel: React.FC<{ accessToken?: string | null }> = ({ accessToken }) =>
 accessToken ? <ConferenceWorkspace key={accessToken} token={accessToken} /> : <Text>Entre novamente para consultar as conferências.</Text>;
const ConferenceWorkspace: React.FC<{ token: string }> = ({ token }) => {
 const [page, setPage] = React.useState<ConferencePage | null>(null);
 const [offset, setOffset] = React.useState(0);
 const [reload, setReload] = React.useState(0);
 const [loading, setLoading] = React.useState(true);
 const [error, setError] = React.useState('');
 const [selected, setSelected] = React.useState<string | null>(null);
 const [saving, setSaving] = React.useState(false);
 const [notice, setNotice] = React.useState('');
 React.useEffect(() => {
  const controller = new AbortController();
  setLoading(true); setPage(null); setSelected(null); setError('');
  listConferences(token, offset, controller.signal).then(value => { if (!controller.signal.aborted) setPage(value); })
   .catch(() => { if (!controller.signal.aborted) setError('Não foi possível consultar as conferências. Atualize para tentar novamente.'); })
   .finally(() => { if (!controller.signal.aborted) setLoading(false); });
  return () => controller.abort();
 }, [token, offset, reload]);
 return <OktonPanel title="Conferências" subtitle="Solicitações enviadas com resposta ou custo ainda não confirmado.">
  <Stack spacing="4">
   <Text>Esta lista não faz conciliação automática. O erro não comprova ausência de cobrança. Confira a evidência antes de concluir uma ocorrência.</Text>
   <Button alignSelf="flex-start" variant="outline" isDisabled={loading || saving} onClick={() => setReload(n => n + 1)}>Atualizar conferências</Button>
   {notice && <Text role="status">{notice}</Text>}
   {loading && <Text role="status">Consultando pendências...</Text>}
   {error && <Text role="alert">{error}</Text>}
   {page?.items.length === 0 && <Text>Nenhuma pendência nesta página.</Text>}
   {page?.items.map(item => <Box borderWidth="1px" borderRadius="md" p="4" key={item.callId}>
    <Stack spacing="2">
     <Text fontWeight="semibold">Empresa {item.scope.companyId} · Filial {item.scope.branchId} · {item.systemId}</Text>
     <Text>{textStatuses[item.state]} · {textMoney(item.reservedMicros)}</Text>
     <Text fontSize="sm">{new Date(item.createdAt).toLocaleString('pt-BR')}</Text>
     <Text fontSize="sm" wordBreak="break-all">Protocolo: {item.callId}</Text>
     <Text fontSize="sm" wordBreak="break-all">Contrato: {item.scope.contractId}</Text>
     <Text>{reasons[item.reason || ''] || 'Ainda não há evidência suficiente para concluir a conferência.'}</Text>
     {item.reason && <Text fontSize="sm">Diagnóstico: {item.reason}</Text>}
     {item.diagnostic ? <Box><Text fontSize="sm">Campos rejeitados (nome e tipo):</Text>{item.diagnostic.fields.map((f, index) => <Text key={index} fontSize="sm">{f.field}: {f.type}</Text>)}</Box>
      : <Text fontSize="sm">Este registro não contém o detalhamento dos campos da resposta.</Text>}
     {item.canReleaseNoSend ? <>
      <Button size="sm" alignSelf="flex-start" isDisabled={saving} onClick={() => setSelected(item.callId)}>Conferir não envio</Button>
      {selected === item.callId && <NoSendReview key={item.callId} token={token} item={item} onBusy={setSaving} onDone={() => { setNotice('Reserva liberada com comprovante e responsável registrados.'); setReload(n => n + 1); }} />}
     </> : <Text fontSize="sm">Liberação por não envio indisponível: este registro não comprova que o provedor deixou de processar a chamada. A apuração do consumo ainda exige evidência do provedor.</Text>}
    </Stack>
   </Box>)}
   <Stack direction="row">
    {offset > 0 && <Button size="sm" isDisabled={loading || saving} onClick={() => setOffset(Math.max(0, offset - 50))}>Mais recentes</Button>}
    {page?.nextOffset != null && <Button size="sm" isDisabled={loading || saving} onClick={() => setOffset(page!.nextOffset!)}>Mais antigas</Button>}
   </Stack>
  </Stack>
 </OktonPanel>;
};
const NoSendReview: React.FC<{ token: string; item: ConferenceItem; onBusy: (value: boolean) => void; onDone: () => void }> = props => {
 const [reference, setReference] = React.useState('');
 const [hash, setHash] = React.useState('');
 const [confirmed, setConfirmed] = React.useState(false);
 const [busy, setBusy] = React.useState(false);
 const [error, setError] = React.useState('');
 const inFlight = React.useRef(false);
 const alive = React.useRef(true);
 React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
 const valid = /^[a-zA-Z0-9_./-]{1,190}$/.test(reference) && /^[a-f0-9]{64}$/.test(hash) && confirmed;
 const submit = async () => {
  if (!valid || inFlight.current) return;
  inFlight.current = true; setBusy(true); props.onBusy(true); setError('');
  let succeeded = false;
  try { await releaseNoSend(props.token, props.item, reference, hash); succeeded = true; }
  catch { if (alive.current) setError('Liberação não confirmada. Preserve o comprovante e atualize as conferências antes de tentar novamente.'); }
  finally { inFlight.current = false; if (alive.current) { setBusy(false); props.onBusy(false); } }
  if (succeeded && alive.current) props.onDone();
 };
 return <Stack spacing="3" borderTopWidth="1px" pt="3">
  <Text>Informe a referência e o SHA-256 do relatório técnico revisado que comprova o não envio. Guarde o relatório no repositório de evidências da Okton; este formulário registra sua referência, não envia o arquivo.</Text>
  <OktonInput name="evidence-reference" label="Referência do comprovante" value={reference} isDisabled={busy} onChange={event => setReference(event.target.value)} />
  <OktonInput name="evidence-sha256" label="SHA-256 do comprovante" value={hash} isDisabled={busy} onChange={event => setHash(event.target.value)} />
  <Checkbox isChecked={confirmed} isDisabled={busy} onChange={event => setConfirmed(event.target.checked)}>Revisei a evidência e confirmo que esta chamada não foi enviada ao provedor.</Checkbox>
  <Button alignSelf="flex-start" isDisabled={!valid || busy} isLoading={busy} onClick={() => void submit()}>Liberar reserva com comprovação</Button>
  {error && <Text role="alert">{error}</Text>}
 </Stack>;
};
